import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import GuestProfileDrawer from './GuestProfileDrawer';
import { useConciergeStore } from '../../store/conciergeStore';
import ConciergeMessageList from './ConciergeMessageList';
import ConciergeComposer from './ConciergeComposer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import * as conciergeApi from '../../api/conciergeApi';

const toIdString = (v) => {
  if (v && typeof v === 'object') {
    if (v._id) return String(v._id);
    if (v.$oid) return String(v.$oid);
  }
  return v != null ? String(v) : '';
};

// === Name/Email heuristics copied from ConciergeThreadItem ===
function isLikelyUid(s) {
  if (!s || typeof s !== 'string') return false;
  const t = s.trim();
  if (!t || t.includes('@') || /\s/.test(t)) return false;
  if (t.length < 20) return false;
  return /^[A-Za-z0-9_-]+$/.test(t);
}
function getEmail(thread) {
  return (
    thread?.profile?.contact?.email ||
    thread?.email ||
    thread?.guestEmail ||
    thread?.userProfile?.email ||
    null
  );
}
function getDisplayName(thread) {
  const email = getEmail(thread);
  const dn = thread?.displayName;
  const fullName = thread?.profile?.identity?.fullName || thread?.guestName || null;
  if (fullName && fullName.trim()) return fullName.trim();
  if (dn && !isLikelyUid(dn)) return dn;
  if (email) {
    const at = String(email).indexOf('@');
    if (at > 0) return String(email).slice(0, at);
    return String(email);
  }
  return thread?.userUid || 'Guest';
};
// Normalize messages to always carry sender.role ('staff' | 'guest') and keep legacy flag
function withNormalizedSender(m) {
  if (!m) return m;
  const role =
    (m?.sender && typeof m.sender === 'object' && (m.sender.role === 'guest' ? 'guest' : (m.sender.role === 'staff' ? 'staff' : undefined))) ||
    (typeof m?.sender === 'string' && (m.sender === 'guest' ? 'guest' : (m.sender === 'staff' ? 'staff' : undefined))) ||
    (typeof m?.fromGuest === 'boolean' && (m.fromGuest ? 'guest' : 'staff')) ||
    (m?.role === 'guest' ? 'guest' : (m?.role === 'staff' ? 'staff' : undefined));
  if (!role) return m;
  return {
    ...m,
    sender: { role },
    fromGuest: role === 'guest',
  };
}

const EMPTY_MESSAGES = Object.freeze([]);
const EMPTY_IDS = Object.freeze([]);

/**
 * ConciergeRoom
 * Right-pane chat window (messages + composer)
 * - Shows active thread messages
 * - Fetches history on thread change
 * - Marks as read when focused
 * - Sends messages via store (optimistic)
 */
export default function ConciergeRoom({ propertyId: propPropertyId, height = '100%' }) {
  const activeThread = useConciergeStore(s => s.activeThread);
  const propertyId = React.useMemo(() => {
    const fromThread = activeThread?.propertyId;
    const raw =
      propPropertyId != null && propPropertyId !== ''
        ? propPropertyId
        : fromThread != null && fromThread !== ''
          ? fromThread
          : localStorage.getItem('activePropertyId');
    return toIdString(raw);
  }, [propPropertyId, activeThread?.propertyId]);
  React.useEffect(() => {
    console.debug('[ConciergeRoom] propertyId resolved', {
      propPropertyId,
      threadPropertyId: activeThread?.propertyId,
      resolved: propertyId,
    });
  }, [propPropertyId, activeThread?.propertyId, propertyId]);

  const fetchHistory = useConciergeStore(s => s.fetchHistory);
  const deleteSelectedMessages = useConciergeStore(s => s.deleteSelectedMessages);
  const emitPresenceHeartbeat = useConciergeStore(s => s.emitPresenceHeartbeat);

  const userUid = activeThread?.userUid;
  const key = threadKeyOf(propertyId, userUid);

  const fetchedKeyRef = React.useRef(null);
  const joinedKeyRef = React.useRef(null);

  const [openProfile, setOpenProfile] = React.useState(false);
  const openGuestDrawer = () => setOpenProfile(true);
  const [roomNumber, setRoomNumber] = React.useState(null);

  const messages = useConciergeStore(
    React.useCallback(
      (s) => (key ? (s.messagesByThread?.[key] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES),
      [key]
    )
  );

  React.useEffect(() => {
    try {
      const state = useConciergeStore.getState?.();
      const keys = state?.messagesByThread ? Object.keys(state.messagesByThread) : [];
      console.debug('[ConciergeRoom] render snapshot', {
        propertyId,
        userUid,
        key,
        hasActiveThread: !!activeThread,
        storeKeys: keys,
        storeLenForKey: key ? state?.messagesByThread?.[key]?.length : undefined,
      });
    } catch (err) {
      console.warn('[ConciergeRoom] snapshot failed', err);
    }
  }, [propertyId, userUid, key, activeThread]);

  const normalizedMessages = React.useMemo(
    () => (Array.isArray(messages) ? messages.map(withNormalizedSender) : messages),
    [messages]
  );

  React.useEffect(() => {
    try {
      console.debug('[ConciergeRoom] messages changed', {
        key,
        rawLength: Array.isArray(messages) ? messages.length : '(not array)',
        sample: Array.isArray(messages) ? messages.slice(0, 3).map((m) => ({
          id: m?.id || m?._id || m?.tempId,
          createdAt: m?.createdAt || m?.timestamp,
          text: m?.text || m?.content,
          sender: m?.sender,
        })) : [],
      });
    } catch (err) {
      console.warn('[ConciergeRoom] log messages failed', err);
    }
  }, [key, messages]);

  React.useEffect(() => {
    try {
      console.debug('[ConciergeRoom] normalizedMessages changed', {
        key,
        length: Array.isArray(normalizedMessages) ? normalizedMessages.length : '(not array)',
        sample: Array.isArray(normalizedMessages) ? normalizedMessages.slice(0, 3).map((m) => ({
          id: m?.id || m?._id || m?.tempId,
          createdAt: m?.createdAt || m?.timestamp,
          text: m?.text || m?.content,
          sender: m?.sender,
        })) : [],
      });
    } catch (err) {
      console.warn('[ConciergeRoom] log normalizedMessages failed', err);
    }
  }, [key, normalizedMessages]);

  const clearSelection = useConciergeStore(s => s.clearSelection);
  const exitSelectionMode = useConciergeStore(s => s.exitSelectionMode);

  // Reactive per-thread selection values
  const selectionActive = useConciergeStore(React.useCallback(
    (s) => (propertyId && userUid ? s.isSelectionMode(propertyId, userUid) : false),
    [propertyId, userUid]
  ));
  const selectedIds = useConciergeStore(React.useCallback(
    (s) => (propertyId && userUid ? (s.getSelection(propertyId, userUid) || EMPTY_IDS) : EMPTY_IDS),
    [propertyId, userUid]
  ));
  const selectedCount = selectedIds.length;

  const presenceState = useConciergeStore(React.useCallback(
    (s) => (key ? s.onlineStatusByThread?.[key] : undefined),
    [key]
  ));

  const [presenceTick, setPresenceTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setPresenceTick((x) => x + 1), 10000);
    return () => clearInterval(id);
  }, []);

  const guestPresence = presenceState?.guest;

  // Fallback to thread-level guest last active if presence map is empty
  const threadGuestLastActiveIso = React.useMemo(() => {
    const t = activeThread;
    const norm = (v) => {
      if (!v) return undefined;
      const p = Date.parse(String(v));
      return Number.isFinite(p) ? new Date(p).toISOString() : String(v);
    };
    return norm(t?.guestLastActiveAt || t?.lastSeen?.guest || t?.lastSeen?.other);
  }, [activeThread]);

  const effectiveGuestPresence = React.useMemo(() => {
    if (guestPresence && (guestPresence.isOnline || guestPresence.lastActive)) return guestPresence;
    if (threadGuestLastActiveIso) return { isOnline: false, lastActive: threadGuestLastActiveIso };
    return undefined;
  }, [guestPresence, threadGuestLastActiveIso]);

  // Only guest presence drives the header status.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const guestPresenceMeta = React.useMemo(() => formatPresenceStatus(effectiveGuestPresence), [effectiveGuestPresence, presenceTick]);

  const headerTitle = getDisplayName(activeThread);
  const headerEmail = getEmail(activeThread);
  const headerInitials = React.useMemo(() => {
    const src = (headerTitle && headerTitle.trim()) || activeThread?.guestEmail || activeThread?.userUid || 'G';
    return src.trim().slice(0, 1).toUpperCase();
  }, [headerTitle, activeThread]);

  const [openConfirmDelete, setOpenConfirmDelete] = React.useState(false);

  const handleDeleteSelected = () => {
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    deleteSelectedMessages({ propertyId, userUid });
    setOpenConfirmDelete(false);
  };

  useEffect(() => {
  }, [activeThread]);

  useEffect(() => {
    if (!key) return;
    if (fetchedKeyRef.current === key) {
      return;
    }
    fetchedKeyRef.current = key;
    const [pid, uid] = String(key).split(':');
    console.debug('[ConciergeRoom] triggering fetchHistory', { key, pid, uid });
    fetchHistory(pid, uid, { page: 1, limit: 50 })
      .then(() => {
        console.debug('[ConciergeRoom] fetchHistory resolved', { key });
      })
      .catch((err) => {
        console.warn('[ConciergeRoom] fetchHistory error', { key, error: err });
      });
  }, [key, fetchHistory]);

  useEffect(() => {
    if (!key) return;
    if (joinedKeyRef.current === key) {
      return;
    }
    joinedKeyRef.current = key;
    const api = useConciergeStore.getState?.();
    const join = api && api.joinThreadRoom;
    const [pid, uid] = String(key).split(':');
    if (typeof join === 'function') {
      try {
        join(pid, uid);
      } catch {}
    }
  }, [key]);

  useEffect(() => {
    if (!key || !Array.isArray(messages)) return;
    // Only derive when the list reference changes or its length changes
    const ids = messages.map(m => m?.id || m?._id || m?.tempId).filter(Boolean);
    const dupes = ids.filter((id, i) => ids.indexOf(id) !== i);
  }, [key, messages]);

  useEffect(() => {
    if (!selectionActive) return; // only listen when active
    const onKey = (e) => {
      if (e.key === 'Escape') {
        try { exitSelectionMode(propertyId, userUid); } catch {}
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectionActive, exitSelectionMode, propertyId, userUid]);

  useEffect(() => {
    if (!propertyId || !userUid || typeof emitPresenceHeartbeat !== 'function') return;
    emitPresenceHeartbeat(propertyId, userUid);
    const id = setInterval(() => {
      try { emitPresenceHeartbeat(propertyId, userUid); } catch {}
    }, 30000);
    return () => clearInterval(id);
  }, [propertyId, userUid, emitPresenceHeartbeat]);

  useEffect(() => {
    let cancelled = false;
    const loadRoomNumber = async () => {
      if (!propertyId || !userUid) {
        if (!cancelled) setRoomNumber(null);
        return;
      }
      try {
        const profile = await conciergeApi.getGuestProfile({ propertyId, userUid });
        let detected = profile?.roomNumber
          || profile?.guest?.roomNumber
          || profile?.booking?.roomNumber
          || null;
        if (!detected) {
          const logs = await conciergeApi.getCheckinLogs({ propertyId, userUid, limit: 10 });
          const arrayLogs = Array.isArray(logs) ? logs : (logs?.logs || logs?.items || []);
          if (Array.isArray(arrayLogs)) {
            const withRoom = arrayLogs.find((log) => log?.roomNumber || log?.result?.booking?.roomNumber || log?.requestBody?.roomNumber);
            detected = withRoom?.roomNumber
              || withRoom?.result?.booking?.roomNumber
              || withRoom?.requestBody?.roomNumber
              || null;
          }
        }
        if (!cancelled) {
          setRoomNumber(detected ? String(detected) : null);
        }
      } catch (e) {
        if (!cancelled) setRoomNumber(null);
      }
    };
    loadRoomNumber();
    return () => {
      cancelled = true;
    };
  }, [propertyId, userUid]);

  if (!activeThread) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">Select a thread to start chatting</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', zIndex: 1 }}>
        <Avatar
          alt={headerTitle || 'Guest'}
          sx={{ width: 36, height: 36, fontSize: 14, cursor: 'pointer' }}
          onClick={openGuestDrawer}
        >
          {headerInitials}
        </Avatar>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, cursor: 'pointer' }}
            onClick={openGuestDrawer}
          >
            {headerTitle}
          </Typography>
          {(guestPresenceMeta?.label || roomNumber) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {guestPresenceMeta?.label ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    role="img"
                    aria-label={guestPresenceMeta.isOnline ? 'Guest online' : 'Guest offline'}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: guestPresenceMeta.isOnline ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: guestPresenceMeta.isOnline ? 'success.main' : 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {guestPresenceMeta.label}
                  </Typography>
                </Box>
              ) : null}
              {roomNumber ? (
                <Chip
                  size="small"
                  label={`Room ${roomNumber}`}
                  sx={{
                    backgroundColor: 'rgba(17,24,39,0.08)',
                    fontWeight: 600,
                    letterSpacing: 0.2,
                  }}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
        {selectionActive ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Chip size="small" label={`${selectedCount} selected`} color="warning" variant="outlined" />
            <Button size="small" variant="text" onClick={() => exitSelectionMode(propertyId, userUid)} sx={{ fontWeight: 600 }}>
              Done
            </Button>
          </Box>
        ) : null}
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, px: 3, py: 2, overflowY: 'auto' }}>
        <ConciergeMessageList
          key={key /* remount list when switching threads to reset scroll/highlight state */}
          propertyId={propertyId}
          thread={activeThread}
          threadKey={key}
          messages={normalizedMessages}
        />
      </Box>

      <Divider />

      {/* Composer */}
      <ConciergeComposer
        propertyId={propertyId}
        thread={activeThread}
        threadKey={key}
        selectionActive={selectionActive}
        selectedIds={selectedIds}
      />
      
      {/* Guest Profile Drawer */}
      <GuestProfileDrawer
        open={openProfile}
        onClose={() => setOpenProfile(false)}
        propertyId={propertyId}
        userUid={activeThread?.userUid}
        fallbackName={headerTitle}
        fallbackEmail={headerEmail}
      />
      <ConfirmDialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Delete messages?"
        description="Delete the selected messages and their attachments. This can’t be undone."
        confirmLabel="Delete"
      />
    </Box>
  );
}

function threadKeyOf(propertyId, userUid) {
  const pid = toIdString(propertyId);
  return (pid && userUid ? `${pid}:${userUid}` : '');
}

function formatPresenceStatus(status) {
  if (!status) return null;

  // Match staff web store expiry window (20,000ms). Avoid the old 2-minute grace.
  const ONLINE_WINDOW_MS = 20_000;

  const lastActiveIso = status.lastActive ? String(status.lastActive) : undefined;
  let lastActiveMs = undefined;
  if (lastActiveIso) {
    const parsed = Date.parse(lastActiveIso);
    if (Number.isFinite(parsed)) lastActiveMs = parsed;
  }

  const now = Date.now();
  const fresh = lastActiveMs != null ? (now - lastActiveMs) <= ONLINE_WINDOW_MS && (now - lastActiveMs) >= 0 : false;

  // Only show Online if the server says online AND the timestamp is fresh.
  const isOnline = !!status.isOnline && fresh;

  if (isOnline) {
    return { label: 'Online', color: 'success', variant: 'filled', isOnline: true };
  }

  if (!lastActiveMs) return null;

  const diffMs = now - lastActiveMs;
  if (diffMs < 0) return null;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) {
    return { label: 'Active just now', color: 'default', variant: 'outlined', isOnline: false };
  }
  if (minutes < 60) {
    return { label: `Active ${minutes}m ago`, color: 'default', variant: 'outlined', isOnline: false };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { label: `Active ${hours}h ago`, color: 'default', variant: 'outlined', isOnline: false };
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return { label: `Active ${days}d ago`, color: 'default', variant: 'outlined', isOnline: false };
  }
  try {
    const formatted = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(lastActiveMs);
    return { label: `Active ${formatted}`, color: 'default', variant: 'outlined', isOnline: false };
  } catch {
    return { label: `Active ${days}d ago`, color: 'default', variant: 'outlined', isOnline: false };
  }
}
